import { Extension } from "../../../../../../types/base";
import { Reference } from "../../../../../../types/reference";

export const COPILOT_SOURCE_URL =
  "https://medial.health/fhir/StructureDefinition/copilot-source";

export const COPILOT_SOURCE_SECTION_URL =
  "https://medial.health/fhir/StructureDefinition/copilot-source-section";

export const COPILOT_APPLIED_AT_URL =
  "https://medial.health/fhir/StructureDefinition/copilot-applied-at";

export const COPILOT_SOURCE_KEY_URL =
  "https://medial.health/fhir/StructureDefinition/copilot-source-key";

export type CopilotSourceSection =
  | "chief-complaint"
  | "hpi"
  | "vitals"
  | "allergies"
  | "family-history"
  | "hospitalizations"
  | "medical-history"
  | "immunizations"
  | "procedures"
  | "diagnoses"
  | "medications"
  | "care-plans"
  | "review-of-systems"
  | "physical-assessment"
  | "social-history"
  | "sexual-history"
  | "prescriptions";

const COPILOT_MANAGED_EXTENSION_URLS = new Set([
  COPILOT_SOURCE_URL,
  COPILOT_SOURCE_SECTION_URL,
  COPILOT_APPLIED_AT_URL,
  COPILOT_SOURCE_KEY_URL,
]);

export function buildCopilotReference(copilotId: string): Reference {
  return {
    type: "Copilot",
    reference: `Copilot/${copilotId}`,
    identifier: copilotId,
    display: "AI Scribe Note",
  };
}

export function buildCopilotSourceExtensions(
  copilotId: string,
  section: CopilotSourceSection,
  options: { sourceKey?: string; appliedAt?: Date } = {},
): Extension[] {
  const appliedAt = options.appliedAt ?? new Date();
  const extensions: Extension[] = [
    {
      url: COPILOT_SOURCE_URL,
      valueReference: buildCopilotReference(copilotId),
    },
    {
      url: COPILOT_SOURCE_SECTION_URL,
      valueCode: section,
    },
    {
      url: COPILOT_APPLIED_AT_URL,
      valueDateTime: appliedAt,
    },
  ];

  if (options.sourceKey) {
    extensions.push({
      url: COPILOT_SOURCE_KEY_URL,
      valueCode: options.sourceKey,
    });
  }

  return extensions;
}

export function stripCopilotManagedExtensions(
  extensions: Extension[] | undefined,
): Extension[] {
  return (extensions ?? []).filter(
    (extension) => !COPILOT_MANAGED_EXTENSION_URLS.has(extension.url),
  );
}

export function mergeCopilotSourceExtensions(
  existingExtensions: Extension[] | undefined,
  copilotId: string,
  section: CopilotSourceSection,
  options: { sourceKey?: string; appliedAt?: Date } = {},
): Extension[] {
  return [
    ...stripCopilotManagedExtensions(existingExtensions),
    ...buildCopilotSourceExtensions(copilotId, section, options),
  ];
}

export function withCopilotSource<T>(
  resource: T,
  copilotId: string,
  section: CopilotSourceSection,
): T & { extension: Extension[] } {
  const existing = (resource as { extension?: Extension[] }).extension;
  return {
    ...resource,
    extension: mergeCopilotSourceExtensions(existing, copilotId, section),
  };
}

export function withCopilotSourceOnItem<T>(
  item: T,
  copilotId: string,
  section: CopilotSourceSection,
  sourceKey: string,
): T & { extension: Extension[] } {
  const existing = (item as { extension?: Extension[] }).extension;
  return {
    ...item,
    extension: mergeCopilotSourceExtensions(existing, copilotId, section, {
      sourceKey,
    }),
  };
}

export function hasCopilotSource(
  resource: { extension?: Extension[] } | null | undefined,
): boolean {
  return (
    resource?.extension?.some(
      (extension) => extension.url === COPILOT_SOURCE_URL,
    ) ?? false
  );
}
